// ============================================================================
// NORTH EMBER PAINTING — CENTRAL SITE CONFIG
// Update business details, services, areas, imagery and content here.
// ============================================================================

export const site = {
  name: "North Ember Painting",
  owner: "Ajmal Faiz Mohammad",
  tagline: "Professional Painting. Beautifully Finished.",
  phone: "+1 (778) 581-5457",
  phoneHref: "tel:+17785815457",
  email: "northemberpainting@gmail.com",
  emailHref: "mailto:northemberpainting@gmail.com",
  address: {
    street: "7938 110 St",
    city: "Delta",
    province: "BC",
    postal: "V4C 4H5",
    country: "CA",
    countryName: "Canada",
  },
  // Set to false to hide the street address site-wide and show service area only.
  showAddress: true,
  // PRODUCTION DOMAIN — leave "" until the real domain is registered. While empty,
  // canonical URLs, og:url, og:image and sitemap absolute URLs are automatically
  // omitted (a wrong canonical is worse than none). Set e.g. "https://northemberpainting.ca"
  // and run: node scripts/generate-sitemap.mjs
  domain: "",
  hours: "Open 24 hours, 7 days a week — call or message any time.",
  hoursShort: "Call or message any time",
  yearsExperience: 7,
  insured: true,
  primaryArea: "Delta, British Columbia",
  // No social profiles yet — icons render only when URLs are added here.
  socials: [],
  // Real customer reviews go here once supplied. Hidden while empty.
  reviews: [],
  // Optional estimate calculator rates. Leave null to keep the calculator hidden.
  calculatorRates: null,
  // Cloudflare Turnstile site key (public). Leave "" to run without captcha widget.
  turnstileSiteKey: "",
};

export const addressLine = `${site.address.street}, ${site.address.city}, ${site.address.province} ${site.address.postal}, ${site.address.countryName}`;

// ---------------------------------------------------------------------------
// IMAGERY — curated concept photography. Replace URLs with real project
// photos as they become available (see docs/UPDATING-CONTENT.md).
// All imagery on the site is labelled as concept imagery until then.
// ---------------------------------------------------------------------------
export const images = {
  hero: {
    url: "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBwYWludCUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTI4MDMxNjN8MA&ixlib=rb-4.1.0&q=85",
    alt: "Luxury living room interior with freshly painted walls and expansive windows (concept image)",
  },
  heroSecondary: {
    url: "https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBwYWludCUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTI4MDMxNjN8MA&ixlib=rb-4.1.0&q=85",
    alt: "Contemporary interior with architectural painted finishes (concept image)",
  },
  livingRoom: {
    url: "https://images.unsplash.com/photo-1646987916641-1f3c8992daa2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBwYWludCUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTI4MDMxNjN8MA&ixlib=rb-4.1.0&q=85",
    alt: "Living room painted in a calm neutral tone with crisp trim work (concept image)",
  },
  openPlan: {
    url: "https://images.pexels.com/photos/13141770/pexels-photo-13141770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Spacious open-plan living room with smooth matte wall finishes (concept image)",
  },
  kitchenCabinets: {
    url: "https://images.unsplash.com/photo-1565183928294-7063f23ce0f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBraXRjaGVuJTIwcGFpbnRlZCUyMGNhYmluZXRzJTIwaW50ZXJpb3IlMjBkZXNpZ258ZW58MHx8fHwxNzkyODAzMTYzfDA&ixlib=rb-4.1.0&q=85",
    alt: "Kitchen with freshly painted navy island and white cabinets (concept image)",
  },
  cabinetsDark: {
    url: "https://images.unsplash.com/photo-1714860534425-7ce04e013dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBraXRjaGVuJTIwcGFpbnRlZCUyMGNhYmluZXRzJTIwaW50ZXJpb3IlMjBkZXNpZ258ZW58MHx8fHwxNzkyODAzMTYzfDA&ixlib=rb-4.1.0&q=85",
    alt: "Matte dark grey painted kitchen cabinets with a factory-style finish (concept image)",
  },
  exteriorModern: {
    url: "https://images.unsplash.com/photo-1787419559815-e2a3623355bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxob3vzZSUyMGV4dGVyaW9yJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwcGFpbnRpbmclMjBjcmFmdHNtYW58ZW58MHx8fHwxNzkyODAzMTcyfDA&ixlib=rb-4.1.0&q=85",
    alt: "Modern home exterior with a deep painted finish and warm door accent (concept image)",
  },
  exteriorPnw: {
    url: "https://images.unsplash.com/photo-1628744448839-a475cc0e90c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxob3VzZSUyMGV4dGVyaW9yJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwcGFpbnRpbmclMjBjcmFmdHNtYW58ZW58MHx8fHwxNzkyODAzMTcyfDA&ixlib=rb-4.1.0&q=85",
    alt: "Pacific Northwest home exterior with freshly painted siding and cedar trim (concept image)",
  },
  exteriorCraftsman: {
    url: "https://images.pexels.com/photos/5502227/pexels-photo-5502227.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Craftsman-style home exterior with a renewed paint finish (concept image)",
  },
  painterRolling: {
    url: "https://images.unsplash.com/photo-1717281234297-3def5ae3eee1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NzN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwYWludGVyJTIwaW50ZXJpb3IlMjB3YWxsJTIwcm9sbGluZyUyMHBhaW50aW5nfGVufDB8fHx8MTc5MjgwMzU2N3ww&ixlib=rb-4.1.0&q=85",
    alt: "Professional painter rolling paint onto an interior wall (illustrative example)",
  },
  painterPrep: {
    url: "https://images.unsplash.com/photo-1652829069834-2c05031199c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NzN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBwYWludGVyJTIwaW50ZXJpb3IlMjB3YWxsJTIwcm9sbGluZyUyMHBhaW50aW5nfGVufDB8fHx8MTc5MjgwMzU2N3ww&ixlib=rb-4.1.0&q=85",
    alt: "Painter preparing a roller and tray with floors protected (illustrative example)",
  },
  painterCeiling: {
    url: "https://images.pexels.com/photos/5691610/pexels-photo-5691610.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Painter using an extension pole for smooth ceiling and wall coats (illustrative example)",
  },
  fenceStaining: {
    url: "https://images.pexels.com/photos/31583249/pexels-photo-31583249.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Protective wood stain being applied to an outdoor cedar fence (concept image)",
  },
  deckStained: {
    url: "https://images.unsplash.com/photo-1792630862829-90f519d52d88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHx3b29kZW4lMjBkZWNrJTIwc3RhaW5pbmclMjBmZW5jZSUyMHBhaW50aW5nfGVufDB8fHx8MTc5MjgwMzU2N3ww&ixlib=rb-4.1.0&q=85",
    alt: "Stained coastal grey wooden deck and perimeter fencing (concept image)",
  },
  commercialOffice: {
    url: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb21tZXJjaWFsJTIwb2ZmaWNlJTIwaW50ZXJpb3IlMjBwYWludGVkJTIwd2FsbHxlbnwwfHx8fDE3OTI4MDM1Njd8MA&ixlib=rb-4.1.0&q=85",
    alt: "Corporate office interior with crisp freshly painted charcoal walls (concept image)",
  },
  commercialRetail: {
    url: "https://images.pexels.com/photos/7534208/pexels-photo-7534208.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Commercial lounge and retail space with high-end painted wall surfaces (concept image)",
  },
  trimDetail: {
    url: "https://images.pexels.com/photos/7601180/pexels-photo-7601180.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Freshly painted white door frame trim and crisp baseboard lines (concept image)",
  },
  trimMolding: {
    url: "https://images.pexels.com/photos/7601158/pexels-photo-7601158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Bright interior with sharp painted trim, crown molding and baseboards (concept image)",
  },
  bedroomGreen: {
    url: "https://images.unsplash.com/photo-1785529907887-bc5620d68570?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjYWxtJTIwYmVkcm9vbSUyMHBhaW50ZWQlMjBhY2NlbnQlMjB3YWxsfGVufDB8fHx8MTc5MjgwMzU3Mnww&ixlib=rb-4.1.0&q=85",
    alt: "Calm bedroom with a deep green painted accent wall (concept image)",
  },
  bedroomNeutral: {
    url: "https://images.unsplash.com/photo-1784315123205-34aa35249165?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjYWxtJTIwYmVkcm9vbSUyMHBhaW50ZWQlMjBhY2NlbnQlMjB3YWxsfGVufDB8fHx8MTc5MjgwMzU3Mnww&ixlib=rb-4.1.0&q=85",
    alt: "Serene bedroom with smooth neutral matte wall paint (concept image)",
  },
  swatchDeck: {
    url: "https://images.pexels.com/photos/24994811/pexels-photo-24994811.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Paint swatch fan deck showing architectural colour options (concept image)",
  },
  swatchCompare: {
    url: "https://images.pexels.com/photos/6474454/pexels-photo-6474454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Comparing paint colour chips and fan deck swatches (concept image)",
  },
  neighbourhood: {
    url: "https://images.unsplash.com/photo-1784559815165-c782fac9cd15?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6Nzd8MHwxfHNlYXJjaHwzfHxzdWJ1cmJhbiUyMHJlc2lkZW50aWFsJTIwaG91c2UlMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2QlMjBvdmVyY2FzdHxlbnwwfHx8fDE3OTI4MDM1NzJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Pacific Northwest residential street under a coastal sky (concept image)",
  },
  neighbourhoodDelta: {
    url: "https://images.unsplash.com/photo-1572523473094-46c0d60ceb29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxzdWJ1cmJhbiUyMHJlc2lkZW50aWFsJTIwaG91c2UlMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2QlMjBvdmVyY2FzdHxlbnwwfHx8fDE3OTI4MDM1NzJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Coastal residential street with well-kept painted homes and mature trees (concept image)",
  },
  ceilingDetail: {
    url: "https://images.unsplash.com/photo-1563973558248-414a6514debe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NTYxOTF8MHwxfHNlYXJjaHwxfHxmcmVzaGx5JTIwcGFpbnRlZCUyMGNlaWxpbmclMjBzbW9vdGglMjB3YWxsJTIwaW50ZXJpb3J8ZW58MHx8fDE3OTI4MDM1NzJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Flawless white ceiling finish with a crisp wall junction (concept image)",
  },
  wallMinimal: {
    url: "https://images.unsplash.com/photo-1744063905275-907e208ff443?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NTYxOTF8MHwxfHNlYXJjaHwyfHxmcmVzaGx5JTIwcGFpbnRlZCUyMGNlaWxpbmclMjBzbW9vdGglMjB3YWxsJTIwaW50ZXJpb3J8ZW58MHx8fDE3OTI4MDM1NzJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Minimalist smooth painted interior wall and ceiling finish (concept image)",
  },
};

export const ogImage = images.hero.url;

// ---------------------------------------------------------------------------
// SERVICES — set enabled: false to hide a service everywhere (nav, pages,
// sitemap). Additional services are prepared but hidden until confirmed.
// ---------------------------------------------------------------------------
export const services = [
  {
    slug: "interior-house-painting",
    name: "Interior House Painting",
    short: "Walls, ceilings and trim finished with careful preparation and clean, lasting lines.",
    icon: "Paintbrush",
    image: "livingRoom",
    enabled: true,
    intro: [
      "A fresh interior changes how your home feels every single day. North Ember Painting provides interior house painting across Delta, Ladner, Tsawwassen and Surrey — with careful preparation, tidy work habits and finishes chosen around how you actually live in each room.",
      "From a single bedroom to a full main-floor repaint, every project starts with protecting your floors and furniture and ends with a walkthrough so you can see the finished result before we pack up.",
    ],
    included: [
      "Walls, ceilings, doors and trim as scoped in your estimate",
      "Filling of minor nail holes and surface imperfections",
      "Sanding and spot-priming of repaired areas",
      "Careful masking and protection of floors, furniture and fixtures",
      "Two-coat coverage where needed for an even, durable finish",
      "Daily tidy-up and a final walkthrough with you",
    ],
    preparation: [
      "Furniture moved toward room centres and covered",
      "Floors protected with drop sheets and paper",
      "Surfaces washed, sanded and dust-free before any coating",
      "Caulking refreshed at trim joints where needed",
    ],
    considerations: [
      "Colour and sheen change how light reads in a room — test samples on the wall before committing",
      "Occupied homes are scheduled room by room so daily life keeps moving",
      "Older plaster or previously damaged walls may need extra preparation time",
      "Low-odour product options are available on request",
    ],
    faqs: [
      { q: "How long does it take to paint a room?", a: "Most standard rooms take one to two days including preparation and drying time between coats. Larger or heavily repaired spaces take longer — your written estimate will outline the expected schedule." },
      { q: "Do I need to move my furniture?", a: "We move and cover furniture as part of our preparation. We ask that you clear small, fragile and personal items before we arrive." },
      { q: "Which sheen should I choose for my walls?", a: "It depends on the room and how it is used. Washable mattes and eggshells suit most living areas, while higher-sheen finishes suit trim and busy spaces. We walk through the options during colour and project planning." },
    ],
  },
  {
    slug: "exterior-house-painting",
    name: "Exterior House Painting",
    short: "Weather-ready exterior finishes built for the coastal BC climate.",
    icon: "Home",
    image: "exteriorPnw",
    enabled: true,
    intro: [
      "Exterior paint in the Pacific Northwest works hard. Rain, moisture and cool winters demand thorough preparation and the right conditions for application. North Ember Painting paints home exteriors across Delta and surrounding communities with preparation-first methods that help finishes last.",
      "We plan exterior projects around weather windows, protect landscaping and walkways while we work, and keep you informed as each elevation is completed.",
    ],
    included: [
      "Siding, trim, fascia, soffits, doors and railings as scoped",
      "Washing and removal of dirt, mildew and loose paint",
      "Scraping, sanding and spot-priming of bare areas",
      "Caulking of gaps and joints where required",
      "Careful masking of windows, roofing and hardscapes",
      "Full cleanup and a final walkthrough",
    ],
    preparation: [
      "Surfaces washed and allowed to dry properly",
      "Loose and failing paint scraped and sanded back",
      "Bare wood spot-primed before topcoats",
      "Landscaping, decks and walkways protected",
    ],
    considerations: [
      "Exterior work is weather-dependent — we schedule around rain and temperature",
      "South and west exposures typically weather faster and may need extra attention",
      "Peeling or chalking surfaces require more preparation and can affect timelines",
      "Strata or neighbourhood colour guidelines should be confirmed before we start",
    ],
    faqs: [
      { q: "When is the best time to paint an exterior in BC?", a: "Late spring through early fall usually offers the most reliable dry, mild weather on the coast. We confirm timing as your project approaches and adjust around the forecast." },
      { q: "How do you handle peeling paint?", a: "Failing paint is scraped and sanded back to a sound edge, bare areas are spot-primed, and only then do topcoats go on. Skipping this step is the most common cause of early failure." },
      { q: "Do you paint stucco and fibre-cement siding?", a: "Yes — we work with the common siding types found in Delta-area homes. Surface-specific preparation and products are discussed during your estimate." },
    ],
  },
  {
    slug: "residential-painting",
    name: "Residential Painting",
    short: "Whole-home painting, inside and out, for houses, townhomes and condos.",
    icon: "Building2",
    image: "openPlan",
    enabled: true,
    intro: [
      "Whether you are settling into a new home, refreshing a well-loved one, or preparing a property for sale, North Ember Painting handles complete residential painting projects across Delta, Ladner, Tsawwassen and Surrey.",
      "One point of contact, one coordinated schedule, and the same standard of preparation whether we are painting a single suite or an entire house inside and out.",
    ],
    included: [
      "Interior and exterior scopes for houses, townhomes and condos",
      "A single coordinated plan and schedule for the whole project",
      "Surface preparation matched to each area of the home",
      "Protection of flooring, landscaping, fixtures and belongings",
      "Consistent colour and sheen documentation for future touch-ups",
      "Final walkthrough and cleanup on completion",
    ],
    preparation: [
      "Room-by-room or elevation-by-elevation sequencing",
      "Surfaces washed, repaired, sanded and primed as needed",
      "Fixtures, hardware and flooring masked and protected",
    ],
    considerations: [
      "Whole-home projects are easiest before move-in or between tenants",
      "Combining interior and exterior work can simplify scheduling",
      "Pre-sale refreshes should focus on entryways, kitchens and main living areas first",
    ],
    faqs: [
      { q: "Can you paint my whole home before I move in?", a: "Yes — empty homes are the fastest and most economical to paint. Share your possession date in your estimate request and we will plan around it." },
      { q: "Do you work in condos and townhomes?", a: "Yes. We are experienced with strata requirements and will coordinate access, parking and elevator bookings where needed." },
      { q: "Can you match my existing colours?", a: "In most cases, yes. Bring a sample or the original colour information and we will confirm the match approach during planning." },
    ],
  },
  {
    slug: "commercial-painting",
    name: "Commercial Painting",
    short: "Offices, retail and commercial spaces painted with minimal disruption.",
    icon: "Briefcase",
    image: "commercialOffice",
    enabled: true,
    intro: [
      "A well-kept commercial space tells customers and staff that details matter. North Ember Painting paints offices, retail units and commercial interiors across Delta and surrounding areas, with scheduling that respects your business hours.",
      "We plan commercial work in phases, communicate clearly with property managers and tenants, and keep work areas clean and safe while your business keeps running.",
    ],
    included: [
      "Office, retail and commercial interior painting",
      "Phased scheduling, including evening or off-hours planning where arranged",
      "Protection of flooring, fixtures, inventory and equipment",
      "Low-odour product options for occupied spaces",
      "Clear communication with owners, managers and tenants",
      "Final walkthrough with the property contact",
    ],
    preparation: [
      "Work areas isolated and protected before coating",
      "Surfaces washed, patched and sanded as scoped",
      "Fixtures, signage and equipment masked or worked around",
    ],
    considerations: [
      "Phased scheduling keeps your space operational during the project",
      "Brand colours should be confirmed with physical samples before full application",
      "Landlord or property manager approvals may be needed for common areas",
    ],
    faqs: [
      { q: "Can you work outside business hours?", a: "We can plan phased or off-hours work where it suits the project. Tell us your constraints in the estimate request and we will build the schedule around them." },
      { q: "Do you paint occupied offices?", a: "Yes. We isolate work areas, protect equipment and use low-odour products where requested to keep disruption to a minimum." },
      { q: "Are you insured for commercial work?", a: "Yes — North Ember Painting is fully insured. Certificates can be discussed directly when we review your project." },
    ],
  },
  {
    slug: "cabinet-painting-refinishing",
    name: "Cabinet Painting & Refinishing",
    short: "Kitchen and built-in cabinets refinished for a smooth, factory-style look.",
    icon: "DoorOpen",
    image: "kitchenCabinets",
    enabled: true,
    intro: [
      "Cabinet refinishing is one of the highest-impact updates a kitchen can get — without the cost and disruption of a full renovation. North Ember Painting refinishes kitchen, bathroom and built-in cabinets across the Delta area with careful prep and smooth, durable finishes.",
      "Doors and drawer fronts are removed, labelled and finished with attention to the details that make cabinetry look factory-fresh: clean edges, even sheen and crisp lines around hardware.",
    ],
    included: [
      "Removal, labelling and reinstallation of doors and drawers",
      "Thorough cleaning and degreasing of all surfaces",
      "Sanding and adhesion priming suited to the existing finish",
      "Smooth sprayed or hand-finished topcoats as scoped",
      "Hardware reinstallation (new hardware installation can be discussed)",
      "Careful protection of counters, floors and appliances",
    ],
    preparation: [
      "Surfaces degreased to remove cooking residue",
      "Existing finish sanded for proper adhesion",
      "Specialty bonding primer applied before topcoats",
      "Curing time respected before doors are rehung",
    ],
    considerations: [
      "Refinishing suits sound cabinets — damaged boxes or doors may need repair first",
      "Kitchens remain partly usable, but plan around a few days of cabinet downtime",
      "Dark-to-light colour changes typically need additional coats",
      "Cured finishes harden further over the first weeks — treat them gently early on",
    ],
    faqs: [
      { q: "How long does cabinet painting take?", a: "A typical kitchen takes several days to a week depending on the number of doors and the condition of the existing finish. Your estimate will include an expected timeline." },
      { q: "Will the finish hold up to daily use?", a: "Proper preparation and quality cabinet-grade coatings produce a durable finish. Like any painted surface, it benefits from reasonable care — especially in the first few weeks of curing." },
      { q: "Can you change the hardware at the same time?", a: "Yes — new knobs and pulls can be installed during reassembly. Mention it in your estimate request so we can plan for any drilling or filling." },
    ],
  },
  {
    slug: "wall-painting",
    name: "Wall Painting",
    short: "Feature walls to full-floor wall repaints with smooth, even coverage.",
    icon: "Roller",
    image: "wallMinimal",
    enabled: true,
    intro: [
      "Walls carry the mood of a room. North Ember Painting provides dedicated wall painting across the Delta area — from a single feature wall to every wall in the house — with smooth coverage, straight cut lines and colours checked against real samples.",
      "We help you think through sheen, natural light and how adjacent rooms connect, so the finished walls feel intentional rather than improvised.",
    ],
    included: [
      "Single walls, feature walls or full-home wall painting",
      "Minor hole and dent filling with sanding",
      "Spot-priming over repairs and stains",
      "Clean cut lines at ceilings, corners and trim",
      "Protection of floors, furniture and adjacent surfaces",
    ],
    preparation: [
      "Walls washed and dust-free before coating",
      "Imperfections filled, sanded and spot-primed",
      "Trim, ceilings and floors masked and protected",
    ],
    considerations: [
      "Deep or bold colours may need an extra coat for full coverage",
      "Flat and matte finishes hide imperfections; higher sheens highlight them",
      "Always check a physical sample on the actual wall in day and evening light",
    ],
    faqs: [
      { q: "Can you paint just one accent wall?", a: "Absolutely. Single-wall and feature-wall projects are welcome and are often completed in a day." },
      { q: "How many coats will my walls need?", a: "Most walls take two coats over sound, similar-coloured paint. Big colour changes or repairs can add a coat — we confirm this in your written estimate." },
      { q: "Can you paint over wallpaper?", a: "We recommend removing wallpaper first for a lasting result. We can discuss the condition of your walls and the best approach during the estimate." },
    ],
  },
  {
    slug: "ceiling-painting",
    name: "Ceiling Painting",
    short: "Flat, bright ceilings with clean junctions and no lap marks.",
    icon: "ArrowUpToLine",
    image: "ceilingDetail",
    enabled: true,
    intro: [
      "Ceilings frame every room, and a stained, yellowed or patchy ceiling drags the whole space down. North Ember Painting paints ceilings across Delta homes with even, streak-free finishes and crisp junctions where wall meets ceiling.",
      "From smooth drywall ceilings to textured surfaces, we match preparation and application to the ceiling you have — and protect everything underneath it while we work.",
    ],
    included: [
      "Smooth and textured ceiling painting",
      "Stain treatment and stain-blocking primer where needed",
      "Crisp junction lines at walls and bulkheads",
      "Full masking and protection of the room below",
      "Two-coat coverage where required",
    ],
    preparation: [
      "Rooms emptied or fully sheeted before overhead work",
      "Water stains treated with appropriate stain-blocking primer",
      "Cracks and minor damage repaired and sanded",
    ],
    considerations: [
      "Water stains must be primed with a stain blocker or they bleed through",
      "Textured (popcorn) ceilings need a condition check before painting",
      "Ceiling work is fastest in unoccupied or cleared rooms",
    ],
    faqs: [
      { q: "Can you paint over a water stain?", a: "Yes — once the source of moisture is fixed. The stain is sealed with a stain-blocking primer before finish coats so it does not reappear." },
      { q: "Do you paint textured ceilings?", a: "Yes, after checking the texture condition. Painted and unpainted texture behave differently, and we will explain the options during your estimate." },
      { q: "What colour should a ceiling be?", a: "Most ceilings suit a flat white or a lighter tint of the wall colour. We can talk through the effect each option has on the room." },
    ],
  },
  {
    slug: "trim-doors-baseboard-painting",
    name: "Trim, Doors & Baseboard Painting",
    short: "Crisp trim, doors and baseboards — the details that make a paint job look finished.",
    icon: "PanelBottom",
    image: "trimDetail",
    enabled: true,
    intro: [
      "Trim, doors and baseboards are where a paint job is won or lost. Sharp lines, smooth enamel finishes and clean caulk joints make a whole home feel cared for. North Ember Painting paints trim, doors, baseboards, casings and crown molding across the Delta area.",
      "This detailed work is available on its own — a fast way to refresh a home — or as part of a larger interior project.",
    ],
    included: [
      "Baseboards, casings, crown molding and wainscoting",
      "Interior doors, frames and closet doors",
      "Caulking of gaps and nail-hole filling",
      "Smooth, durable trim-appropriate finishes",
      "Careful cutting-in and masking for sharp lines",
    ],
    preparation: [
      "Trim cleaned and sanded for adhesion",
      "Gaps caulked and holes filled before coating",
      "Floors and walls masked at every edge",
    ],
    considerations: [
      "Older oil-based trim paint needs proper preparation before water-based topcoats",
      "Semi-gloss and satin sheens are standard for durability and cleaning",
      "Door painting is easiest when doors can be removed and finished flat",
    ],
    faqs: [
      { q: "Can you paint only the trim and doors?", a: "Yes — trim-only refreshes are common and make a noticeable difference, especially before selling or after new flooring." },
      { q: "What finish is best for baseboards?", a: "A satin or semi-gloss enamel is the usual choice: it cleans easily and stands up to vacuum bumps and mopping." },
      { q: "My trim is stained wood — can it be painted?", a: "Yes. Stained trim is cleaned, sanded and primed with a bonding primer before topcoats for a durable painted finish." },
    ],
  },
  {
    slug: "fence-painting-staining",
    name: "Fence Painting & Staining",
    short: "Fences protected and refreshed against coastal weather.",
    icon: "Fence",
    image: "fenceStaining",
    enabled: true,
    intro: [
      "Fences take the full force of Delta's wet winters and summer sun. A properly prepared and stained fence resists moisture, greying and early rot — and sharply improves how a yard looks. North Ember Painting paints and stains wood fences across Delta, Ladner, Tsawwassen and Surrey.",
      "We match the product approach to your fence: solid and semi-transparent stains for weathered cedar, or paint for previously painted boards.",
    ],
    included: [
      "Fence washing and mildew removal",
      "Loose boards and minor repairs flagged before coating",
      "Stain or paint application matched to the wood condition",
      "Protection of plantings, lawns and adjacent surfaces",
      "Gates, posts and caps finished as scoped",
    ],
    preparation: [
      "Surfaces washed and given proper drying time",
      "Greyed or loose fibres removed where needed",
      "Vegetation tied back and protected",
    ],
    considerations: [
      "Wood must be dry before staining — scheduling follows the weather",
      "New pressure-treated fences should weather before their first coating",
      "Shared fences usually need a neighbour conversation first — we can advise",
    ],
    faqs: [
      { q: "Paint or stain — which is better for my fence?", a: "Stain soaks in and lets wood breathe, which suits most cedar fences. Paint gives a solid colour but needs more maintenance over time. We will recommend based on your fence's condition." },
      { q: "How often should a fence be re-stained in BC?", a: "Coastal exposure typically means every few years, depending on sun and rain exposure. We can assess yours and suggest a maintenance rhythm." },
      { q: "Can you work on a shared fence?", a: "Yes, with both neighbours informed. It is worth agreeing on colour and cost-sharing before the project starts." },
    ],
  },
  {
    slug: "deck-painting-staining",
    name: "Deck Painting & Staining",
    short: "Decks cleaned, prepared and finished for the seasons ahead.",
    icon: "Layers",
    image: "deckStained",
    enabled: true,
    intro: [
      "A deck is where coastal summers happen — and where moisture, foot traffic and UV do their worst. North Ember Painting cleans, prepares, stains and paints wood decks across the Delta area, restoring colour and adding protection before the weather takes its toll.",
      "We pay particular attention to the surfaces that fail first: horizontal boards, stair treads and railings.",
    ],
    included: [
      "Deck washing and surface preparation",
      "Sanding of weathered or splintering areas as scoped",
      "Stain or solid deck coating application",
      "Railings, stairs, skirting and posts as scoped",
      "Protection of siding, doors and surrounding planting",
    ],
    preparation: [
      "Decks washed and fully dried before coating",
      "Failed previous coatings removed where practical",
      "Loose fasteners and damaged boards flagged for repair",
    ],
    considerations: [
      "Horizontal deck surfaces wear fastest and may need more frequent maintenance",
      "Previously painted decks usually suit solid coatings rather than transparent stains",
      "Dry weather windows are essential — we schedule around the forecast",
    ],
    faqs: [
      { q: "How long before we can use the deck again?", a: "Most deck finishes need at least a day or two before foot traffic and longer before furniture goes back. We will give you specific guidance for the product used." },
      { q: "Can you stain over old stain?", a: "Often yes, if the old coating is sound and compatible. Peeling or flaking surfaces need more preparation first — we assess this during the estimate." },
      { q: "Do you repair damaged boards?", a: "We flag damaged or rotten boards before finishing so they can be replaced. Larger carpentry repairs are discussed case by case." },
    ],
  },
  {
    slug: "repainting-renovation-painting",
    name: "Repainting & Renovation Painting",
    short: "Painting that keeps pace with your renovation, repair or refresh.",
    icon: "Hammer",
    image: "painterRolling",
    enabled: true,
    intro: [
      "Renovations move fast, and painting is the trade that makes every other trade's work look finished. North Ember Painting provides repainting and renovation painting across the Delta area — after drywall work, kitchen updates, water damage repairs or a long-overdue refresh.",
      "We coordinate with your contractor or timeline, handle the patching-to-paint sequence properly, and leave renovated spaces clean and ready to enjoy.",
    ],
    included: [
      "Painting after drywall, millwork and repair work",
      "Skim-coat touch-ups and sanding of patched areas as scoped",
      "Priming of new drywall, patches and bare surfaces",
      "Whole-room or whole-home repainting",
      "Sequencing with your renovation schedule",
    ],
    preparation: [
      "New drywall and patches primed before finish coats",
      "Dust from construction removed from surfaces",
      "Floors, cabinets and fixtures protected before coating",
    ],
    considerations: [
      "Paint schedules around other trades — share your renovation timeline early",
      "New drywall needs proper priming or patches will flash through the finish",
      "Post-renovation dust control matters; we clean surfaces before we coat them",
    ],
    faqs: [
      { q: "Can you paint right after the drywall is done?", a: "Once drywall compound is fully dry and sanded, yes. We prime new and patched surfaces first so the finish coat goes on evenly." },
      { q: "Do you work with my general contractor?", a: "Yes — we regularly coordinate scheduling with contractors and homeowners to keep renovations moving." },
      { q: "Can you match paint in rooms we are not renovating?", a: "We can usually get very close with a sample. For perfect uniformity across connected spaces, repainting the full sightline is sometimes the better option — we will advise honestly." },
    ],
  },
  // ----- Prepared but hidden until the owner confirms them -----
  {
    slug: "drywall-patching-prep",
    name: "Drywall Patching & Prep",
    short: "Patches, repairs and surface preparation before painting.",
    icon: "Hammer",
    image: "painterPrep",
    enabled: false,
    intro: [], included: [], preparation: [], considerations: [], faqs: [],
  },
  {
    slug: "rental-turnovers",
    name: "Rental Turnover Painting",
    short: "Fast, tidy repaints between tenants.",
    icon: "KeyRound",
    image: "openPlan",
    enabled: false,
    intro: [], included: [], preparation: [], considerations: [], faqs: [],
  },
  {
    slug: "strata-property-management",
    name: "Strata & Property Management Painting",
    short: "Coordinated painting for strata councils and property managers.",
    icon: "Buildings",
    image: "commercialRetail",
    enabled: false,
    intro: [], included: [], preparation: [], considerations: [], faqs: [],
  },
  {
    slug: "office-retail-painting",
    name: "Office & Retail Painting",
    short: "Focused painting programs for offices and retail spaces.",
    icon: "Store",
    image: "commercialRetail",
    enabled: false,
    intro: [], included: [], preparation: [], considerations: [], faqs: [],
  },
  {
    slug: "colour-consultation",
    name: "Colour Consultation",
    short: "Guided colour selection for your home or business.",
    icon: "Palette",
    image: "swatchDeck",
    enabled: false,
    intro: [], included: [], preparation: [], considerations: [], faqs: [],
  },
];

export const enabledServices = services.filter((s) => s.enabled);
export const getService = (slug) => services.find((s) => s.slug === slug && s.enabled);

// ---------------------------------------------------------------------------
// SERVICE AREAS
// ---------------------------------------------------------------------------
export const areas = [
  {
    slug: "delta",
    name: "Delta",
    title: "Painters in Delta, BC",
    image: "neighbourhoodDelta",
    intro: [
      "Delta is home. North Ember Painting is based in North Delta and serves every part of the municipality — from the established streets of Sunshine Hills and Scottsdale to the newer communities near Nordel and the waterfront neighbourhoods to the south.",
      "Much of Delta's housing dates from the 1960s through the 1980s — ranchers, split-levels and two-storeys that reward careful repainting — alongside a steady stream of new construction. Whether your home is a Sunshine Hills original or a recent build, we know the surfaces, the styles and the weather they face.",
    ],
    neighbourhoods: ["North Delta", "Sunshine Hills", "Scottsdale", "Nordel", "Annieville", "Delta Manor"],
    exteriorNote: "Delta's coastal climate means long wet seasons and mild winters. Exterior finishes here succeed or fail on preparation: washing away mildew, scraping back loose paint and priming bare wood before topcoats. South and west elevations weather fastest and deserve the closest look during an assessment.",
    servicesNote: "Our most requested Delta projects are full interior repaints of 1970s–80s homes, exterior repaints of stucco and siding, and kitchen cabinet refinishing.",
    faqs: [
      { q: "Are you based in Delta?", a: "Yes — North Ember Painting operates from 7938 110 St in North Delta, and Delta is our primary service area." },
      { q: "Do you paint older Delta homes?", a: "Regularly. Many local homes date from the 1960s–80s and need thoughtful preparation — especially exteriors and previously painted trim. That preparation is a core part of how we work." },
    ],
  },
  {
    slug: "ladner",
    name: "Ladner",
    title: "Painters in Ladner, BC",
    image: "exteriorCraftsman",
    intro: [
      "Ladner's village character is worth protecting. From heritage-era cottages near the harbour to family homes along tree-lined streets and properties edging the farmland, North Ember Painting provides interior and exterior painting throughout Ladner.",
      "Older Ladner homes often carry layers of paint history — original trim, settled walls and additions from different decades. We take the time to prepare these surfaces properly so the new finish respects the home's character.",
    ],
    neighbourhoods: ["Ladner Village", "Ladner Harbour", "Hawthorne", "Port Guichon", "Westham Island area"],
    exteriorNote: "Proximity to the Fraser River and surrounding farmland keeps humidity high for much of the year. Mildew washing, careful drying time and sound caulking matter here more than almost anywhere — especially on shaded north elevations and homes near the water.",
    servicesNote: "Ladner projects often involve exterior repaints of character homes, trim and baseboard refreshes, and interior repaints that brighten older, compartmentalized floor plans.",
    faqs: [
      { q: "Do you work on older or heritage-style homes in Ladner?", a: "Yes. Older homes get extra preparation time and a conversation about which details to preserve or highlight. For designated heritage properties, any approval requirements are the owner's responsibility, but we are happy to work within them." },
      { q: "How does riverside humidity affect my paint?", a: "Persistent moisture encourages mildew and slows drying. We wash and treat affected surfaces, schedule around dry weather and use products suited to damp coastal conditions." },
    ],
  },
  {
    slug: "tsawwassen",
    name: "Tsawwassen",
    title: "Painters in Tsawwassen, BC",
    image: "exteriorModern",
    intro: [
      "Tsawwassen's seaside setting is beautiful — and demanding on paint. Salt air, strong sun on south-facing slopes and open exposure to the Strait of Georgia mean exteriors here need the right preparation and products. North Ember Painting paints homes across Tsawwassen, from Beach Grove cottages to newer homes near Tsawwassen Springs.",
      "Inside, Tsawwassen homes tend toward bright, open spaces where natural light changes dramatically through the day — something we account for when helping you test and choose colours.",
    ],
    neighbourhoods: ["Beach Grove", "English Bluff", "Tsawwassen Springs", "Cliff Drive", "Pebble Hill"],
    exteriorNote: "Salt air and UV exposure accelerate fading and chalking, particularly on south and west faces and homes close to the water. Exteriors in Tsawwassen benefit from thorough washing, sound priming and finish coats chosen for coastal exposure.",
    servicesNote: "Common Tsawwassen requests include exterior repaints for sun-exposed elevations, deck and fence staining, and light, coastal-inspired interior palettes.",
    faqs: [
      { q: "Does living near the water change how my exterior should be painted?", a: "Yes. Salt and sun exposure call for diligent washing and preparation, and finishes suited to coastal conditions. South and west elevations may need attention sooner than sheltered sides." },
      { q: "Can you help choose colours for a bright, open home?", a: "Yes — we always recommend testing physical samples on the wall, since strong natural light shifts how colours read. Our colour palette tool is a good place to start exploring." },
    ],
  },
  {
    slug: "surrey",
    name: "Surrey",
    title: "Painters in Surrey, BC",
    image: "neighbourhood",
    intro: [
      "Surrey is one of the fastest-growing cities in BC, and its housing reflects that energy — townhome complexes, new developments in Clayton and Grandview, and established neighbourhoods in Guildford and Fleetwood. North Ember Painting serves North Surrey and surrounding districts from our base just across the border in Delta.",
      "Townhome and strata-adjacent work is a particular fit here: we are used to coordinating with property managers, respecting complex rules and keeping shared spaces clean while we work.",
    ],
    neighbourhoods: ["Guildford", "Fleetwood", "Fraser Heights", "Clayton", "Whalley", "Newton"],
    exteriorNote: "Surrey's newer housing mixes stucco, fibre-cement board and vinyl-accented elevations, each with its own preparation needs. In established areas, mature trees keep elevations shaded and damp — a recipe for mildew that proper washing and priming resolves.",
    servicesNote: "Surrey projects frequently include townhome interior repaints, cabinet refinishing in newer kitchens, and exterior work on fibre-cement and stucco homes.",
    faqs: [
      { q: "Do you paint townhomes in Surrey?", a: "Yes — townhome interiors and exteriors are a regular part of our work. For strata-managed exteriors, we coordinate with your property manager as needed." },
      { q: "Which parts of Surrey do you cover?", a: "Our primary coverage is North Surrey — Guildford, Fleetwood, Fraser Heights, Clayton, Whalley and Newton. For other districts, contact us and we will confirm availability." },
    ],
  },
];

export const getArea = (slug) => areas.find((a) => a.slug === slug);

// ---------------------------------------------------------------------------
// PROCESS
// ---------------------------------------------------------------------------
export const processSteps = [
  { title: "Initial Inquiry", text: "Call, message or send the estimate form any time. Share the basics — what you want painted, where, and when you are hoping to start." },
  { title: "Project Discussion", text: "We talk through your goals, colours you are considering, timing and any concerns, so the estimate reflects what you actually want." },
  { title: "On-Site Assessment", text: "When a project needs it, we visit to measure, check surface conditions and spot anything that affects preparation or price." },
  { title: "Written Estimate", text: "You receive a clear written estimate covering scope, preparation, materials approach and schedule — no vague allowances." },
  { title: "Colour & Project Planning", text: "We confirm colours with real samples, agree on the sequence of work, and lock in dates that suit your household or business." },
  { title: "Surface Preparation", text: "The unglamorous work that makes finishes last: washing, scraping, sanding, filling, caulking, priming and protecting everything that is not being painted." },
  { title: "Painting & Finishing", text: "Coats go on in the right order with proper drying time — ceilings, walls, then trim and details — with tidy work areas at the end of every day." },
  { title: "Cleanup & Final Walkthrough", text: "We remove all protection, clean the work areas and walk the project with you. Anything that is not right gets addressed before we call it done." },
];

// ---------------------------------------------------------------------------
// PRICING FACTORS (no rates — honest cost education)
// ---------------------------------------------------------------------------
export const pricingFactors = [
  { title: "Surface area", text: "The single biggest driver: more wall, ceiling or siding means more preparation, material and time." },
  { title: "Number of rooms", text: "Each room adds setup, masking, cutting-in and cleanup, even when rooms are small." },
  { title: "Surface condition", text: "Sound, clean surfaces paint quickly. Damage, stains, peeling or heavy texture add preparation hours." },
  { title: "Preparation & repairs", text: "Filling, sanding, caulking and priming are where lasting finishes are made — and where rushed quotes cut corners." },
  { title: "Paint & material selection", text: "Product lines vary in price and performance. We discuss options honestly rather than defaulting to the cheapest." },
  { title: "Access & height", text: "Vaulted ceilings, stairwells and tall exteriors need ladders, scaffolding or extra time to reach safely." },
  { title: "Interior vs exterior", text: "Exterior work adds washing, weather windows and exposure-rated products to the equation." },
  { title: "Project complexity", text: "Colour changes, detailed trim work, cabinets and occupied spaces all influence the time a project takes." },
  { title: "Scheduling", text: "Flexible timing can help; compressed timelines or phased commercial work are planned and priced accordingly." },
];

// ---------------------------------------------------------------------------
// GLOBAL FAQs
// ---------------------------------------------------------------------------
export const globalFaqs = [
  { q: "How much does painting cost?", a: "Every project is different — cost depends on surface area, condition, preparation, access and materials. We provide a clear written estimate before any work begins, so you know exactly what is included. See our pricing expectations page for the factors that shape a quote." },
  { q: "Are you insured?", a: "Yes. North Ember Painting is fully insured. If you need documentation for a strata, landlord or property manager, ask when we discuss your project." },
  { q: "Which areas do you serve?", a: "We are based in Delta, BC and serve Delta, Ladner, Tsawwassen and Surrey. If you are nearby but outside these areas, contact us and we will confirm whether we can help." },
  { q: "How soon can you start?", a: "Scheduling depends on the season and current bookings. Exterior work also follows the weather. Share your preferred timing in the estimate form and we will tell you honestly what is possible." },
  { q: "Do I need to buy the paint?", a: "No — materials are included in your estimate unless you prefer otherwise. We discuss product options during planning and never substitute without telling you." },
  { q: "How should I prepare my home before you arrive?", a: "We handle furniture moving, covering and surface preparation. It helps if you clear small, fragile and personal items from the rooms being painted and arrange pet care for work days." },
  { q: "Do you offer a warranty?", a: "For details about workmanship commitments on your specific project, please contact us directly — we are happy to discuss them before you book." },
  { q: "Can you paint my kitchen cabinets?", a: "Yes — cabinet painting and refinishing is one of our specialties. Doors and drawers are removed, prepared and refinished for a smooth, durable result." },
  { q: "Do you paint exteriors in winter?", a: "Exterior painting needs dry surfaces and suitable temperatures, so most exterior work happens between spring and fall on the coast. Interior projects run year-round." },
  { q: "What should I expect on work days?", a: "We arrive as scheduled, protect your floors and belongings, keep tools organized, and tidy up daily. You get clear communication throughout, and a final walkthrough before we consider the job complete." },
];

// ---------------------------------------------------------------------------
// PORTFOLIO — concept imagery until real project photos are supplied.
// Set concept: false only for genuine North Ember Painting projects.
// ---------------------------------------------------------------------------
export const portfolioCategories = ["All", "Interior", "Exterior", "Cabinets", "Commercial", "Trim & Detail"];

export const portfolio = [
  { id: "p1", title: "Layered Neutral Living Room", category: "Interior", image: "livingRoom", caption: "Warm neutral walls with crisp white trim — concept image.", concept: true },
  { id: "p2", title: "Open-Plan Main Floor", category: "Interior", image: "openPlan", caption: "Smooth matte walls across an open living space — concept image.", concept: true },
  { id: "p3", title: "Deep Green Bedroom Accent", category: "Interior", image: "bedroomGreen", caption: "A single deep accent wall transforms a calm bedroom — concept image.", concept: true },
  { id: "p4", title: "Soft Neutral Bedroom", category: "Interior", image: "bedroomNeutral", caption: "Serene neutral matte finish with elegant detailing — concept image.", concept: true },
  { id: "p5", title: "Navy Island Kitchen", category: "Cabinets", image: "kitchenCabinets", caption: "Two-tone cabinet refinishing with a navy island — concept image.", concept: true },
  { id: "p6", title: "Charcoal Cabinet Refresh", category: "Cabinets", image: "cabinetsDark", caption: "Matte dark grey cabinets with a factory-style finish — concept image.", concept: true },
  { id: "p7", title: "Modern Ember-Door Exterior", category: "Exterior", image: "exteriorModern", caption: "Deep exterior body colour with a warm door accent — concept image.", concept: true },
  { id: "p8", title: "Coastal Siding Renewal", category: "Exterior", image: "exteriorPnw", caption: "Pacific Northwest siding and cedar trim renewal — concept image.", concept: true },
  { id: "p9", title: "Craftsman Exterior Refresh", category: "Exterior", image: "exteriorCraftsman", caption: "Craftsman-style exterior paint renewal — concept image.", concept: true },
  { id: "p10", title: "Office Corridor in Charcoal", category: "Commercial", image: "commercialOffice", caption: "Commercial interior with deep charcoal feature walls — concept image.", concept: true },
  { id: "p11", title: "Retail Lounge Finish", category: "Commercial", image: "commercialRetail", caption: "High-end painted surfaces in a commercial lounge — concept image.", concept: true },
  { id: "p12", title: "Crisp Trim & Wainscoting", category: "Trim & Detail", image: "trimDetail", caption: "Sharp door casings, wainscoting and baseboard lines — concept image.", concept: true },
  { id: "p13", title: "Crown Molding Detail", category: "Trim & Detail", image: "trimMolding", caption: "Crown molding and baseboard detail in a bright interior — concept image.", concept: true },
  { id: "p14", title: "Flawless Ceiling Finish", category: "Interior", image: "ceilingDetail", caption: "Clean ceiling plane with a perfect wall junction — concept image.", concept: true },
  { id: "p15", title: "Cedar Fence Staining", category: "Exterior", image: "fenceStaining", caption: "Protective stain on an outdoor cedar fence — concept image.", concept: true },
  { id: "p16", title: "Coastal Grey Deck", category: "Exterior", image: "deckStained", caption: "Stained deck and perimeter fencing in coastal grey — concept image.", concept: true },
];

// ---------------------------------------------------------------------------
// COLOUR PALETTES (inspirational names — not tied to any paint brand)
// ---------------------------------------------------------------------------
export const palettes = [
  {
    id: "light",
    name: "Light & Airy",
    blurb: "Bright, open palettes that make the most of coastal daylight.",
    colors: [
      { name: "Tsawwassen Sand", hex: "#EDE6D8" },
      { name: "Morning Mist", hex: "#E8ECEF" },
      { name: "Alabaster Glow", hex: "#F5F1E8" },
      { name: "Harbour Fog", hex: "#DDE4E7" },
      { name: "Soft Linen", hex: "#EFE9DF" },
      { name: "Pale Driftwood", hex: "#D9CFC0" },
    ],
  },
  {
    id: "neutral",
    name: "Warm Neutrals",
    blurb: "Grounded, livable tones that pair with wood and natural light.",
    colors: [
      { name: "Fraser Greige", hex: "#B8AE9E" },
      { name: "Warm Clay", hex: "#C29B7B" },
      { name: "Oatfield", hex: "#CBBFA5" },
      { name: "Cedar Bark", hex: "#8A6F57" },
      { name: "Stone Path", hex: "#9B978C" },
      { name: "Toasted Almond", hex: "#D2B48C" },
    ],
  },
  {
    id: "bold",
    name: "Bold & Deep",
    blurb: "Confident accent colours for feature walls, doors and cabinetry.",
    colors: [
      { name: "Ember Flame", hex: "#C4501B" },
      { name: "Deep Inlet", hex: "#1F3A4D" },
      { name: "Forest Dusk", hex: "#2E4036" },
      { name: "Charcoal Slate", hex: "#33393F" },
      { name: "Burnished Gold", hex: "#B98A2F" },
      { name: "Midnight Current", hex: "#16233A" },
    ],
  },
];

// ---------------------------------------------------------------------------
// FORM OPTIONS (kept in sync with backend validation)
// ---------------------------------------------------------------------------
export const formOptions = {
  propertyTypes: [
    ["single-family", "Single-family house"],
    ["townhouse", "Townhouse"],
    ["condo", "Condo"],
    ["apartment", "Apartment"],
    ["commercial", "Commercial property"],
    ["other", "Other"],
  ],
  services: [
    ...enabledServices.map((s) => [s.slug, s.name]),
    ["multiple", "Multiple services"],
    ["not-sure", "Not sure yet"],
  ],
  interiorExterior: [
    ["interior", "Interior"],
    ["exterior", "Exterior"],
    ["both", "Both"],
    ["not-sure", "Not sure yet"],
  ],
  projectSizes: [
    ["small", "Small — a room or two"],
    ["medium", "Medium — several rooms / partial exterior"],
    ["large", "Large — whole home / full exterior"],
    ["not-sure", "Not sure"],
  ],
  surfaceConditions: [
    ["good", "Good — minor wear only"],
    ["fair", "Fair — some damage or staining"],
    ["poor", "Poor — peeling, damage or heavy wear"],
    ["not-sure", "Not sure"],
  ],
  startDates: [
    ["asap", "As soon as possible"],
    ["1-3-months", "Within 1–3 months"],
    ["3-6-months", "Within 3–6 months"],
    ["flexible", "Flexible"],
  ],
  contactMethods: [
    ["phone", "Phone call"],
    ["text", "Text message"],
    ["email", "Email"],
  ],
  budgets: [
    ["not-sure", "Prefer not to say / not sure"],
    ["under-2000", "Under $2,000"],
    ["2000-5000", "$2,000 – $5,000"],
    ["5000-10000", "$5,000 – $10,000"],
    ["over-10000", "Over $10,000"],
  ],
};
